import groupBy from 'lodash/groupBy';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentOrPreviousGroup } from '../../../../../../api/groups';
import { Module } from '../../../../../../api/module';
import useThrowAsyncError from '../../../../../../hooks/useThrowAsyncError';
import { getLessonResults, LessonResults } from '../../../api/results';
import { ScorableItem } from '../../../api/scorable-items';
import { SchoolYearValue, useResultsDataContext } from '../../../context/ResultsDataContext';
import { TableData } from '../../types';
import { useFilters } from '../../../context/filter-context/FilterContext';
import {
  buildDetailViewGroupedTableRows,
  buildTableRows,
  createTableHeader,
  createTableHeaderForDetailView,
} from './table-data-utils';

async function fetchLessonResults(
  selectedSchoolYear: SchoolYearValue,
  selectedModule: Module,
  scorableItems: Record<string, Record<string, ScorableItem[]>>,
  selectedGroups: CurrentOrPreviousGroup[],
) {
  const getLessonResultsSearchParams = new URLSearchParams({
    includeFreeExercises: 'true',
    includeCompletedAssignments: 'true',
    includeUncompletedAssignments: 'true',
    exerciseIds: scorableItems[selectedSchoolYear.id][selectedModule.id].map(item => item.id).join(','),
  });

  const lessonsData = await Promise.all(
    selectedGroups.map(group => getLessonResults(selectedModule!.id, group.id, getLessonResultsSearchParams)),
  );

  return lessonsData.reduce((acc, lessonData, index) => {
    acc[selectedGroups[index].id] = lessonData;
    return acc;
  }, {} as Record<string, LessonResults>);
}

export default function useTableData(): {
  data: TableData;
  loading: boolean;
  noGroups: boolean;
  noGroupsForFilter: boolean;
  noScorableItems: boolean;
  noScorableItemsForFilter: boolean;
  detailView: boolean;
  usedScorableItems: (ScorableItem & { isAssignment: boolean })[];
} {
  const { t } = useTranslation();
  const { groups, scorableItemsPerSchoolYearPerModule, members, fetchMembersForGroups, loading, scorableItems } =
    useResultsDataContext();
  const { selectedModule, selectedSchoolYear, selectedSchool, exerciseIds, selectedMembers, selectedGroups } =
    useFilters();
  const [resultsLoading, setResultsLoading] = useState(true);
  const [lessonDataPerGroup, setLessonDataPerGroup] = useState<Record<string, LessonResults>>({});
  const handleError = useThrowAsyncError();

  useEffect(() => {
    if (!loading.members && selectedGroups.some(group => !members[group.id])) fetchMembersForGroups(selectedGroups);
  }, [selectedGroups, fetchMembersForGroups, loading.members, members]);

  const displayedGroups = useMemo(() => {
    return (selectedGroups || []).filter(
      x => x.acceptedMemberCount > 0 && x.schoolYear === selectedSchoolYear?.id && x.school.id === selectedSchool?.id,
    );
  }, [selectedGroups, selectedSchoolYear?.id, selectedSchool?.id]);

  const sortedSelectedGroupString = useMemo(() => {
    return selectedGroups
      .map(g => g.id)
      .sort()
      .join(',');
  }, [selectedGroups]);

  useEffect(() => {
    let current = true;

    async function fetchScoreData() {
      if (Object.values(loading).some(x => x)) return;
      if (!selectedSchoolYear || !selectedModule) return;
      if (!scorableItemsPerSchoolYearPerModule[selectedSchoolYear.id]?.[selectedModule.id]) {
        setLessonDataPerGroup({});
        setResultsLoading(false);
        return;
      }

      setResultsLoading(true);

      try {
        const newLessonDataPerGroup = await fetchLessonResults(
          selectedSchoolYear,
          selectedModule,
          scorableItemsPerSchoolYearPerModule,
          selectedGroups,
        );

        if (current) {
          setLessonDataPerGroup(newLessonDataPerGroup);
        }
      } catch (error) {
        handleError(error);
      } finally {
        if (current) {
          setResultsLoading(false);
        }
      }
    }

    fetchScoreData();

    return () => {
      current = false;
    };
  }, [
    selectedModule,
    selectedSchoolYear,
    selectedSchool,
    selectedGroups,
    loading,
    scorableItemsPerSchoolYearPerModule,
    handleError,
    sortedSelectedGroupString,
  ]);

  const flattenedScoresByExercise = useMemo(() => {
    return groupBy(
      Object.values(lessonDataPerGroup).flatMap(lessonData =>
        Object.values(lessonData.scores)
          .flatMap(memberMap => Object.values(memberMap))
          .flat(),
      ),
      'exerciseId',
    );
  }, [lessonDataPerGroup]);

  const usedScorableItems = useMemo(() => {
    if (
      !selectedSchoolYear ||
      !selectedModule ||
      loading.scorableItems ||
      !scorableItemsPerSchoolYearPerModule[selectedSchoolYear.id] ||
      !scorableItemsPerSchoolYearPerModule[selectedSchoolYear.id][selectedModule.id]
    )
      return [];

    return scorableItemsPerSchoolYearPerModule[selectedSchoolYear.id][selectedModule.id]
      .filter(item => !exerciseIds.length || exerciseIds.some(id => id.includes(item.id)))
      .flatMap(item => {
        const isAssignmentSet = new Set<boolean>();
        const items = flattenedScoresByExercise[item.id] || [];

        const filteredItems = exerciseIds.filter(x => x.includes(item.id));

        if (filteredItems.length) {
          const isAssignmentRequested = filteredItems.some(x => x.includes('-true'));
          const isFreeExerciseRequested = filteredItems.some(x => x.includes('-false'));

          items.forEach(score => {
            if (isAssignmentRequested && score.isAssignment) isAssignmentSet.add(score.isAssignment);
            if (isFreeExerciseRequested && !score.isAssignment) isAssignmentSet.add(score.isAssignment);
          });
        } else {
          items.forEach(score => {
            isAssignmentSet.add(score.isAssignment);
          });
        }

        return Array.from(isAssignmentSet).map(isAssignment => ({
          ...item,
          isAssignment,
        }));
      });
  }, [
    flattenedScoresByExercise,
    scorableItemsPerSchoolYearPerModule,
    selectedSchoolYear,
    selectedModule,
    loading.scorableItems,
    exerciseIds,
  ]);

  const dataIsLoading = useMemo(() => {
    return (
      loading.groups ||
      loading.members ||
      loading.scorableItems ||
      resultsLoading ||
      !selectedModule ||
      !selectedSchoolYear
    );
  }, [loading, resultsLoading, selectedModule, selectedSchoolYear]);

  const detailView = exerciseIds.length === 1 && usedScorableItems.length === 1;

  const tableData: TableData = useMemo(() => {
    if (dataIsLoading) {
      return {
        header: createTableHeader(t, usedScorableItems),
        rows: [],
      };
    }

    if (detailView) {
      return {
        header: createTableHeaderForDetailView(t),
        rows: buildDetailViewGroupedTableRows(
          lessonDataPerGroup,
          displayedGroups,
          selectedMembers,
          usedScorableItems[0],
        ),
      };
    }

    return {
      header: createTableHeader(t, usedScorableItems),
      rows: buildTableRows(lessonDataPerGroup, displayedGroups, selectedMembers, usedScorableItems),
    };
  }, [dataIsLoading, detailView, t, usedScorableItems, lessonDataPerGroup, displayedGroups, selectedMembers]);

  return {
    noGroups: groups.filter(x => x.acceptedMemberCount > 0).length === 0,
    noGroupsForFilter: !selectedGroups.length,
    noScorableItems: scorableItems.length === 0,
    noScorableItemsForFilter: !usedScorableItems.length,
    data: tableData,
    loading: dataIsLoading,
    detailView,
    usedScorableItems,
  };
}
